import React, { Fragment, useState } from 'react';
import {
  PayInvoiceModal,
  ShipmentInvoice
} from 'src/components/ShipmentInvoice';
import invoiceImage from 'src/images/invoice.svg';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Icon,
  Row,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  useApp
} from 'straightline-ui';
import { formatMoney } from 'straightline-utils/accounting';
import {
  DOCUMENT_TYPE,
  INVOICE_STATUS,
  PAYMENT_METHOD
} from 'straightline-utils/constants';
import { formatDate, formatDistanceToNowStrict } from 'straightline-utils/time';

export default function ShipmentInvoiceView({ shipment }) {
  const app = useApp();
  const [paymentInvoice, setPaymentInvoice] = useState(null);

  const {
    result: shipmentInvoices,
    isLoading: invoicesLoading,
    isError: invoicesError
  } = app.service('api/shipment-invoices').useFind(
    {
      $include: ['payment_method', 'shipment'],
      query: { shipment_id: shipment.id, $sort: { created_at: -1 } }
    },
    { keepPreviousData: true }
  );

  const {
    result: podDocument,
    isLoading: podLoading,
    isError: podError
  } = app.service('api/shipment-documents').useFindOne(
    {
      query: {
        shipment_id: shipment.id,
        '$document.document_type$': DOCUMENT_TYPE.PROOF_OF_DELIVERY,
        $sort: { created_at: -1 }
      }
    },
    { keepPreviousData: true }
  );

  const { result: paymentMethods } = app.service('api/payment-methods').useFind(
    {
      query: {
        $limit: 20,
        disabled_at: null,
        type: { $in: [PAYMENT_METHOD.CARD, PAYMENT_METHOD.BANK_ACCOUNT] },
        $sort: { default_method: -1, updated_at: -1 }
      }
    },
    { keepPreviousData: true }
  );

  if (invoicesLoading || podLoading) {
    return null;
  }

  if (invoicesError || podError) {
    return <Alert color="info">There is no invoice yet.</Alert>;
  }

  if (!shipmentInvoices.total) {
    return (
      <Row>
        <Col xl="4" className="order-xl-2">
          <Card className="">
            <CardBody>
              This shipment has not been invoiced yet. The invoice will be
              available after delivery and confirming all documents.
            </CardBody>
          </Card>
        </Col>
        <Col xl="8">
          <div
            style={{
              opacity: 0.4,
              width: '100%',
              overflow: 'hidden',
              maskImage:
                'linear-gradient(to bottom, black 50%, transparent 100%)'
            }}
          >
            <img
              src={invoiceImage}
              style={{ width: '100%' }}
              alt="No Results"
            />
          </div>
        </Col>
      </Row>
    );
  }

  const validPaymentMethods = paymentMethods?.data.filter((method) => {
    if (!method.exp_month || !method.exp_year) {
      return true;
    }
    const methodExpDate = new Date(
      Number(method.exp_year),
      Number(method.exp_month) - 1,
      1
    );
    const now = new Date();
    // Set to end of month
    methodExpDate.setMonth(methodExpDate.getMonth() + 1);
    methodExpDate.setDate(0);
    return methodExpDate > now;
  });

  const latestInvoice = shipmentInvoices.data[0];

  const openPaymentInvoice = (invoice) => {
    setPaymentInvoice(invoice);
  };

  const closePaymentInvoice = () => {
    setPaymentInvoice(null);
  };

  const handlePayInvoice = async ({
    shipment_invoice_id,
    payment_method_id
  }) => {
    await app.service('shipper-api/shipment-invoice-charges').create({
      shipment_invoice_id,
      payment_method_id
    });
    app.service('api/shipment-invoices').invalidateGet(shipment_invoice_id);
    app.service('api/shipment-invoices').invalidateFind();
    app.service('api/shipment-invoice-charges').invalidateFind();
    toast.success('Payment successfully processed.');
    setPaymentInvoice(null);
  };

  return (
    <Fragment>
      <Row>
        <Col xl="4" className="order-xl-2">
          <Card>
            {latestInvoice.status === INVOICE_STATUS.OPEN && (
              <CardBody className="border-bottom">
                <Row className="d-flex justify-content-between mb-2">
                  <Col className="mb-1">
                    <div className="h3 mb-0 d-flex align-items-center">
                      <Icon type="dollar-sign" className="mr-3"></Icon>Payment
                    </div>
                  </Col>
                  <Col className="col-auto mb-1">
                    {new Date(latestInvoice.due_date) <= new Date() ? (
                      <div className="mb-0 text-danger">Overdue</div>
                    ) : (
                      <div className="mb-0 text-primary">
                        Due {formatDistanceToNowStrict(latestInvoice.due_date)}
                      </div>
                    )}
                  </Col>
                </Row>
                <div className="mb-3">
                  Select a payment method to complete your invoice. Additional
                  processing fees may apply. We'll show any fees before you
                  submit payment.
                </div>
                <div>
                  {validPaymentMethods?.length === 0 ? (
                    <Button color="primary" block to="/settings/billing">
                      Add Payment Method
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      block
                      disabled={
                        latestInvoice.paid_amount >= latestInvoice.due_amount
                      }
                      onClick={() => openPaymentInvoice(latestInvoice)}
                    >
                      Pay Invoice
                    </Button>
                  )}
                </div>
              </CardBody>
            )}
            <CardBody>
              <Row className="d-flex justify-content-between mb-2">
                <Col className="mb-1">
                  <div className="h3 mb-0 d-flex align-items-center">
                    <Icon type="file" className="mr-3"></Icon>Documents
                  </div>
                </Col>
                <Col className="col-auto mb-1">
                  <div className="mb-0 text-secondary">
                    Updated{' '}
                    {formatDistanceToNowStrict(latestInvoice.created_at)}
                  </div>
                </Col>
              </Row>
              <div className="mb-3">
                Download the latest invoice and POD for this shipment. Documents
                are updated automatically as new billing or delivery info
                becomes available.
              </div>
              <div>
                <Button
                  color="white"
                  block
                  tag="a"
                  href={`${latestInvoice.secure_url}?download=true`}
                >
                  Download Invoice
                </Button>
                {podDocument && (
                  <Button
                    color="white"
                    block
                    tag="a"
                    href={`${podDocument.document.download_url}`}
                  >
                    Download POD
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl="8">
          {shipmentInvoices.data.map((shipmentInvoice, index) => {
            return (
              <div className="" key={shipmentInvoice.id}>
                {index !== 0 && (
                  <Card
                    className="d-inline-block"
                    style={{
                      marginBottom: '-5px'
                    }}
                  >
                    <CardBody className="py-3">
                      <Button
                        color="white"
                        tag="a"
                        href={`${shipmentInvoice.secure_url}?download=true`}
                      >
                        Download
                      </Button>
                      {shipmentInvoice.status === INVOICE_STATUS.OPEN &&
                        validPaymentMethods?.length > 0 && (
                          <Button
                            color="primary"
                            className="ml-2"
                            disabled={
                              shipmentInvoice.paid_amount >=
                              shipmentInvoice.due_amount
                            }
                            onClick={() => openPaymentInvoice(shipmentInvoice)}
                          >
                            Pay Invoice
                          </Button>
                        )}
                    </CardBody>
                  </Card>
                )}
                <ShipmentInvoice shipmentInvoice={shipmentInvoice} />
                {shipmentInvoice.charges && (
                  <Card className="p-4">
                    <h2>Payments</h2>
                    <div className="table-responsive">
                      <Table className="mb-0">
                        <TableHead>
                          <TableRow>
                            <TableHeader className="px-1 bg-transparent border-top-0">
                              <span className="h6">Payment</span>
                            </TableHeader>
                            <TableHeader className="px-1 bg-transparent border-top-0">
                              <span className="h6">Amount</span>
                            </TableHeader>
                            <TableHeader className="px-1 bg-transparent border-top-0">
                              <span className="h6">Fee</span>
                            </TableHeader>
                            <TableHeader className="px-1 bg-transparent border-top-0">
                              <span className="h6">Date</span>
                            </TableHeader>
                            <TableHeader className="px-1 bg-transparent border-top-0 text-right">
                              <span className="h6">Status</span>
                            </TableHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {shipmentInvoice.charges.map((charge) => (
                            <TableRow key={charge.id}>
                              <TableData className="px-1">
                                <div>
                                  {charge.payment_method.description ||
                                    'Payment Method'}{' '}
                                  -{' '}
                                  {charge.payment_method.reference_number ||
                                    '****'}
                                </div>
                                <div>
                                  {charge.notes || (
                                    <span className="text-muted">
                                      Not Available
                                    </span>
                                  )}
                                </div>
                              </TableData>
                              <TableData className="px-1">
                                {formatMoney(charge.amount)}
                              </TableData>
                              <TableData className="px-1">
                                {formatMoney(charge.fee_amount || 0)}
                              </TableData>
                              <TableData className="px-1">
                                {formatDate(charge.created_at)}
                              </TableData>
                              <TableData className="px-1 text-right">
                                {charge.refunded ? (
                                  <Badge color="soft-danger">REFUNDED</Badge>
                                ) : (
                                  <Badge color="soft-success">PAID</Badge>
                                )}
                              </TableData>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="border-top border-top-2 pt-4 d-flex justify-content-between">
                      <div>
                        <h3 className="mb-0">Total Paid</h3>
                      </div>
                      <div>
                        <h3 className="mb-0">
                          {formatMoney(
                            shipmentInvoice.charges.reduce((total, charge) => {
                              return charge.refunded
                                ? total
                                : total + charge.amount + charge.fee_amount;
                            }, 0)
                          )}
                        </h3>
                      </div>
                    </div>
                    <div className="pt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <div>Payment Amount</div>
                        <div>
                          {formatMoney(
                            shipmentInvoice.charges.reduce((total, charge) => {
                              return charge.refunded
                                ? total
                                : total + charge.amount;
                            }, 0)
                          )}
                        </div>
                      </div>
                      <div className="d-flex justify-content-between mb-3">
                        <div>Payment Fees</div>
                        <div>
                          {formatMoney(
                            shipmentInvoice.charges.reduce((total, charge) => {
                              return charge.refunded
                                ? total
                                : total + charge.fee_amount;
                            }, 0)
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            );
          })}
        </Col>
      </Row>
      <PayInvoiceModal
        open={!!paymentInvoice}
        onCancel={closePaymentInvoice}
        onPay={handlePayInvoice}
        shipmentInvoice={paymentInvoice}
        paymentMethods={validPaymentMethods}
      />
    </Fragment>
  );
}
